import React, { useRef } from 'react';
import { TrendingUp, TrendingDown, ChevronUp, ChevronDown, MoveUp, MoveDown, Plus, RefreshCw, Edit2, Check, X, Trash2, ArrowUpDown, ArrowUp, ArrowDown, FileSpreadsheet, Upload, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

// Sort icon helper - renders the appropriate sort icon based on column state
const renderSortIcon = (sortColumn, sortDirection, column) => {
  if (sortColumn !== column) {
    return <ArrowUpDown className="w-4 h-4 text-slate-400" />;
  }
  return sortDirection === 'asc' 
    ? <ArrowUp className="w-4 h-4 text-slate-900" />
    : <ArrowDown className="w-4 h-4 text-slate-900" />;
};

// Panel move buttons - standalone component
const PanelMoveButtons = ({ panelId, movePanelUp, movePanelDown, isFirst, isLast, lightTheme = false }) => (
  <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
    <Button
      variant="ghost"
      size="sm"
      onClick={() => movePanelUp(panelId)}
      disabled={isFirst}
      className={`p-1 h-auto ${isFirst ? 'opacity-30 cursor-not-allowed' : lightTheme ? 'text-white/60 hover:text-white hover:bg-white/20' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200'}`}
      title="Move panel up"
    >
      <MoveUp className="w-4 h-4" />
    </Button>
    <Button
      variant="ghost"
      size="sm"
      onClick={() => movePanelDown(panelId)}
      disabled={isLast}
      className={`p-1 h-auto ${isLast ? 'opacity-30 cursor-not-allowed' : lightTheme ? 'text-white/60 hover:text-white hover:bg-white/20' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200'}`}
      title="Move panel down"
    >
      <MoveDown className="w-4 h-4" />
    </Button>
  </div>
);

const YourStocksPanel = ({
  stocks,
  sortedStocks,
  showStockList,
  toggleStockList,
  selectedPlatform,
  setSelectedPlatform,
  mobilePreview,
  formatCurrencyPrivate,
  lastUpdate,
  isLoading,
  handleRefresh,
  setIsAddingStock,
  movePanelUp,
  movePanelDown,
  isFirst,
  isLast,
  // Sorting props
  sortColumn,
  sortDirection,
  handleSort,
  // Editing props
  editingId,
  editForm,
  setEditForm,
  handleEditStart,
  handleEditCancel,
  handleEditSave,
  // Stock actions
  handleRefreshSingleStock,
  handleDeleteStock,
  refreshingStockId,
  // Metrics
  calculateMetrics,
  // Viewer mode (read-only)
  isViewer = false,
  // Import/Export handlers
  onExport,
  onImport,
  isExporting = false,
  isImporting = false,
}) => {
  // Hidden file input ref for import
  const fileInputRef = useRef(null);

  // Handle file selection for import
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && onImport) {
      onImport(file);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  return (
    <Card className="border-2 border-slate-200 shadow-md rounded-xl overflow-hidden mb-2 sm:mb-4 scroll-mt-32 sm:scroll-mt-0">
      {/* Mobile Header - Clickable to expand/collapse */}
      <div 
        className="sm:hidden bg-gradient-to-r from-emerald-600 to-teal-600 px-3 py-3 cursor-pointer"
        onClick={toggleStockList}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white">
                {selectedPlatform ? `${selectedPlatform} Stocks` : 'Your Stocks and Investments'}
              </h2>
              <p className="text-[10px] text-white/70">{stocks.length} positions</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {selectedPlatform && (
              <button
                onClick={(e) => { e.stopPropagation(); setSelectedPlatform(null); }}
                className="px-2 py-1 bg-white/20 rounded text-[10px] text-white font-semibold"
              >
                Clear
              </button>
            )}
            {/* Add Stock Button - Mobile (hidden for viewers) */}
            {!isViewer && (
            <button
              onClick={(e) => { e.stopPropagation(); setIsAddingStock(true); }}
              className="w-8 h-8 flex items-center justify-center bg-white/30 rounded-lg text-white"
            >
              <Plus className="w-4 h-4" />
            </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); handleRefresh(); }}
              disabled={isLoading}
              className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg text-white"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg text-white">
              {showStockList ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Desktop Header */}
      <CardHeader className={`hidden sm:block bg-gradient-to-r from-emerald-600 to-teal-600 ${mobilePreview ? 'pb-2 px-3 py-2' : 'pb-3 px-5 py-3'} cursor-pointer`} onClick={toggleStockList}>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <PanelMoveButtons panelId="stocks" movePanelUp={movePanelUp} movePanelDown={movePanelDown} isFirst={isFirst} isLast={isLast} lightTheme={true} />
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle className={`font-bold text-white ${mobilePreview ? 'text-base' : 'text-xl'}`}>
                {selectedPlatform ? `${selectedPlatform} Stocks` : 'Your Stocks and Investments'}
              </CardTitle>
              <p className="text-xs text-white/70">{stocks.length} positions in your portfolio</p>
            </div>
            {selectedPlatform && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => { e.stopPropagation(); setSelectedPlatform(null); }}
                className="text-white border-white/50 hover:bg-white/20 h-6 px-2 text-xs"
              >
                Clear Filter
              </Button>
            )}
          </div>
          <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
            <span className={`text-white/70 ${mobilePreview ? 'text-[10px]' : 'text-xs'}`}>
              Last updated: {lastUpdate.toLocaleString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                hour: 'numeric', 
                minute: '2-digit',
                hour12: true 
              })}
            </span>
            {/* Import/Export buttons - Desktop only, hidden for viewers */}
            {!mobilePreview && !isViewer && (
              <>
                {/* Hidden file input for import */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".xlsx,.xls"
                  className="hidden"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isImporting}
                  className="text-white border-white/50 hover:bg-white/20"
                  title="Import stocks from CSV"
                >
                  <Upload className={`w-4 h-4 mr-2 ${isImporting ? 'animate-pulse' : ''}`} />
                  Import
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onExport}
                  disabled={isExporting || stocks.length === 0}
                  className="text-white border-white/50 hover:bg-white/20"
                  title="Export stocks to CSV"
                >
                  <Download className={`w-4 h-4 mr-2 ${isExporting ? 'animate-spin' : ''}`} />
                  Export
                </Button>
              </>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoading}
              className="text-white border-white/50 hover:bg-white/20"
            >
              <RefreshCw className={`${mobilePreview ? 'w-3 h-3' : 'w-4 h-4'} ${isLoading ? 'animate-spin' : ''} ${mobilePreview ? '' : 'mr-2'}`} />
              {!mobilePreview && <span>Refresh Prices</span>}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); toggleStockList(); }}
              className="text-white/80 hover:text-white hover:bg-white/20 p-1 h-auto"
            >
              {showStockList ? (
                <ChevronUp className={`${mobilePreview ? 'w-4 h-4' : 'w-5 h-5'}`} />
              ) : (
                <ChevronDown className={`${mobilePreview ? 'w-4 h-4' : 'w-5 h-5'}`} />
              )}
            </Button>
          </div>
        </div>
      </CardHeader>

      {showStockList && (
        <CardContent className="p-0">
          <div className={mobilePreview ? '' : 'overflow-x-auto'}>
            <table className={`w-full ${mobilePreview ? 'text-xs' : ''}`}>
              <thead className={`bg-gradient-to-r from-slate-200 to-slate-100 border-b-2 border-slate-300 ${mobilePreview ? 'text-[10px]' : ''}`}>
                <tr>
                  <th 
                    className={`text-left py-1.5 font-bold text-slate-700 uppercase tracking-wide cursor-pointer hover:bg-slate-200/50 transition-colors ${mobilePreview ? 'px-1' : 'px-3 sm:px-4 py-3 text-xs'}`}
                    onClick={() => handleSort('symbol')}
                  >
                    <div className="flex items-center gap-1">
                      Sym
                      {renderSortIcon(sortColumn, sortDirection, 'symbol')}
                    </div>
                  </th>
                  <th 
                    className={`text-left py-1.5 font-bold text-slate-700 uppercase tracking-wide cursor-pointer hover:bg-slate-200/50 transition-colors ${mobilePreview ? 'hidden' : 'px-3 sm:px-4 py-3 text-xs hidden sm:table-cell'}`}
                    onClick={() => handleSort('platform')}
                  >
                    <div className="flex items-center gap-2">
                      Platform
                      {renderSortIcon(sortColumn, sortDirection, 'platform')}
                    </div>
                  </th>
                  <th 
                    className={`text-left py-1.5 font-bold text-slate-700 uppercase tracking-wide cursor-pointer hover:bg-slate-200/50 transition-colors ${mobilePreview ? 'hidden' : 'px-3 sm:px-4 py-3 text-xs hidden lg:table-cell'}`}
                    onClick={() => handleSort('investmentType')}
                  >
                    <div className="flex items-center gap-2">
                      Type
                      {renderSortIcon(sortColumn, sortDirection, 'investmentType')}
                    </div>
                  </th>
                  <th className={`text-right font-bold text-slate-700 uppercase tracking-wide ${mobilePreview ? 'hidden' : 'py-3 px-3 sm:px-4 text-xs hidden md:table-cell'}`}>Shares</th>
                  <th className={`text-right font-bold text-slate-700 uppercase tracking-wide ${mobilePreview ? 'hidden' : 'py-3 px-3 sm:px-4 text-xs hidden lg:table-cell'}`}>Cost Basis</th>
                  <th className={`text-right font-bold text-slate-700 uppercase tracking-wide ${mobilePreview ? 'hidden' : 'py-3 px-3 sm:px-4 text-xs hidden md:table-cell'}`}>Current Price</th>
                  <th className={`text-right font-bold text-slate-700 uppercase tracking-wide ${mobilePreview ? 'hidden' : 'py-3 px-3 sm:px-4 text-xs hidden lg:table-cell'}`}>52W High</th>
                  <th 
                    className={`text-right py-1.5 font-bold text-slate-700 uppercase tracking-wide cursor-pointer hover:bg-slate-200/50 transition-colors ${mobilePreview ? 'px-1' : 'px-3 sm:px-4 py-3 text-xs'}`}
                    onClick={() => handleSort('totalValue')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      {mobilePreview ? 'Val' : <><span className="hidden sm:inline">Total</span> Value</>}
                      {renderSortIcon(sortColumn, sortDirection, 'totalValue')}
                    </div>
                  </th>
                  <th 
                    className={`text-right py-1.5 font-bold text-slate-700 uppercase tracking-wide cursor-pointer hover:bg-slate-200/50 transition-colors ${mobilePreview ? 'px-1' : 'px-3 sm:px-4 py-3 text-xs'}`}
                    onClick={() => handleSort('gainLoss')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      {mobilePreview ? 'P&L' : <><span className="hidden sm:inline">Gain/</span>Loss</>}
                      {renderSortIcon(sortColumn, sortDirection, 'gainLoss')}
                    </div>
                  </th>
                  <th className={`text-center py-1.5 font-bold text-slate-700 uppercase tracking-wide ${mobilePreview ? 'px-0.5' : 'px-3 sm:px-4 py-3 text-xs'}`}>{mobilePreview ? '' : 'Actions'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sortedStocks.map((stock) => {
                  const metrics = calculateMetrics(stock);
                  const isPositive = parseFloat(metrics.gainLoss) >= 0;
                  const isEditing = editingId === stock.id;

                  return (
                    <tr key={stock.id} className="hover:bg-slate-50 transition-colors">
                      <td className={mobilePreview ? 'py-1.5 px-1 align-top' : 'py-2 sm:py-3 px-3 sm:px-4'}>
                        <div>
                          {/* Line 1: Symbol + Name on same line */}
                          <div className={`${mobilePreview ? 'text-[10px]' : 'text-sm'}`}>
                            <a 
                              href={`https://www.google.com/finance/quote/${stock.symbol}:${stock.symbol.includes('BINANCE:') ? 'CRYPTO' : 'NASDAQ'}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-bold text-blue-600 hover:text-blue-800 hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {stock.symbol}
                            </a>
                            {mobilePreview && <span className="text-slate-500 ml-1">{stock.name}</span>}
                          </div>
                          {/* Line 2: Platform */}
                          {mobilePreview ? (
                            <div className="text-slate-500 text-[9px]">{stock.platform}</div>
                          ) : (
                            <>
                              <div className="text-xs text-slate-500">{stock.name}</div>
                              <div className="text-xs text-slate-600 sm:hidden mt-0.5">
                                {stock.platform || 'Unknown'}
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                      <td className={`py-2 sm:py-3 px-3 sm:px-4 ${mobilePreview ? 'hidden' : 'hidden sm:table-cell'}`}>
                        {isEditing ? (
                          <Input
                            value={editForm.platform}
                            onChange={(e) => setEditForm({ ...editForm, platform: e.target.value })}
                            className="h-8 text-sm w-32"
                            placeholder="Platform"
                          />
                        ) : (
                          <span className="inline-block px-2 py-1 text-xs font-medium bg-slate-100 text-slate-700 rounded">
                            {stock.platform || 'Unknown'}
                          </span>
                        )}
                      </td>
                      <td className={`py-2 sm:py-3 px-3 sm:px-4 ${mobilePreview ? 'hidden' : 'hidden lg:table-cell'}`}>
                        {isEditing ? (
                          <select
                            value={editForm.investmentType || 'STOCK'}
                            onChange={(e) => setEditForm({ ...editForm, investmentType: e.target.value })}
                            className="h-8 text-sm w-28 px-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                          >
                            <optgroup label="Market">
                              <option value="STOCK">Stock</option>
                              <option value="BOND">Bond</option>
                              <option value="MUNICIPAL">Municipal</option>
                              <option value="BULLION">Bullion</option>
                              <option value="CRYPTO">Crypto</option>
                            </optgroup>
                            <optgroup label="Non-Market">
                              <option value="CASH">Cash</option>
                              <option value="JEWELRY">Jewelry</option>
                              <option value="REAL ESTATE">Real Estate</option>
                              <option value="VEHICLES">Vehicles</option>
                              <option value="PRIVATE INVESTMENTS">Private</option>
                            </optgroup>
                          </select>
                        ) : (
                          <span className={`inline-block px-2 py-1 text-xs font-medium rounded ${
                            ['STOCK', 'BOND', 'MUNICIPAL', 'BULLION', 'CRYPTO'].includes(stock.investmentType || 'STOCK')
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {stock.investmentType || 'STOCK'}
                          </span>
                        )}
                      </td>
                      <td className={`py-2 sm:py-3 px-3 sm:px-4 text-right ${mobilePreview ? 'hidden' : 'hidden md:table-cell'}`}>
                        {isEditing ? (
                          <Input
                            type="number"
                            value={editForm.shares}
                            onChange={(e) => setEditForm({ ...editForm, shares: e.target.value })}
                            className="h-8 text-sm w-24 ml-auto text-right"
                          />
                        ) : (
                          <span className="font-medium text-slate-900 text-sm">{stock.shares.toLocaleString()}</span>
                        )}
                      </td>
                      <td className={`py-2 sm:py-3 px-3 sm:px-4 text-right ${mobilePreview ? 'hidden' : 'hidden lg:table-cell'}`}>
                        {isEditing ? (
                          <Input
                            type="number"
                            step="0.01"
                            value={editForm.costBasis}
                            onChange={(e) => setEditForm({ ...editForm, costBasis: e.target.value })}
                            className="h-8 text-sm w-24 ml-auto text-right"
                          />
                        ) : (
                          <span className="text-slate-700 text-sm">${formatCurrencyPrivate(stock.costBasis)}</span>
                        )}
                      </td>
                      <td className={`py-2 sm:py-3 px-3 sm:px-4 text-right ${mobilePreview ? 'hidden' : 'hidden md:table-cell'}`}>
                        <span className="font-medium text-slate-900 text-sm">${formatCurrencyPrivate(stock.currentPrice)}</span>
                      </td>
                      <td className={`py-2 sm:py-3 px-3 sm:px-4 text-right ${mobilePreview ? 'hidden' : 'hidden lg:table-cell'}`}>
                        <div className="text-right">
                          <span className="text-slate-700 text-sm">${formatCurrencyPrivate(stock.week52High || stock.currentPrice)}</span>
                          {stock.week52High && stock.currentPrice < stock.week52High && (
                            <div className="text-xs text-slate-500">
                              -{((1 - (stock.currentPrice / stock.week52High)) * 100).toFixed(1)}% from high
                            </div>
                          )}
                        </div>
                      </td>
                      <td className={`text-right ${mobilePreview ? 'py-1.5 px-1 align-top' : 'py-2 sm:py-3 px-3 sm:px-4'}`}>
                        <span className={`font-bold text-slate-900 ${mobilePreview ? 'text-[10px]' : 'text-sm'}`}>${formatCurrencyPrivate(metrics.totalValue)}</span>
                      </td>
                      <td className={`text-right ${mobilePreview ? 'py-1.5 px-1 align-top' : 'py-2 sm:py-3 px-3 sm:px-4'}`}>
                        {mobilePreview ? (
                          <div className="text-right">
                            <div className={`font-bold ${isPositive ? 'text-emerald-600' : 'text-red-600'} text-[10px]`}>
                              {isPositive ? '+' : ''}${formatCurrencyPrivate(metrics.gainLoss)}
                            </div>
                            <div className={`${isPositive ? 'text-emerald-600' : 'text-red-600'} text-[9px]`}>
                              {metrics.gainLossPercent}%
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            {isPositive ? (
                              <TrendingUp className="w-3 sm:w-4 h-3 sm:h-4 text-emerald-600" />
                            ) : (
                              <TrendingDown className="w-3 sm:w-4 h-3 sm:h-4 text-red-600" />
                            )}
                            <div className="text-right">
                              <div className={`font-semibold ${isPositive ? 'text-emerald-600' : 'text-red-600'} text-sm`}>
                                ${formatCurrencyPrivate(metrics.gainLoss)}
                              </div>
                              <div className={`${isPositive ? 'text-emerald-600' : 'text-red-600'} text-xs`}>
                                {metrics.gainLossPercent}%
                              </div>
                            </div>
                          </div>
                        )}
                      </td>
                      <td className={mobilePreview ? 'py-1.5 px-0.5 align-top' : 'py-2 sm:py-3 px-3 sm:px-4'}>
                        <div className={`flex items-center justify-center ${mobilePreview ? 'gap-0' : 'gap-1'}`}>
                          {isEditing ? (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditSave(stock)}
                                className={`text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 ${mobilePreview ? 'h-6 w-6 p-0' : 'h-8 w-8'}`}
                              >
                                <Check className={mobilePreview ? 'w-3 h-3' : 'w-4 h-4'} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={handleEditCancel}
                                className={`text-slate-400 hover:text-slate-600 hover:bg-slate-100 ${mobilePreview ? 'h-6 w-6 p-0' : 'h-8 w-8'}`}
                              >
                                <X className={mobilePreview ? 'w-3 h-3' : 'w-4 h-4'} />
                              </Button>
                            </>
                          ) : (
                            <>
                              {!mobilePreview && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleRefreshSingleStock(stock.id, stock.symbol)}
                                  disabled={refreshingStockId === stock.id}
                                  className="h-8 w-8 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50"
                                  title="Refresh price"
                                >
                                  <RefreshCw className={`w-4 h-4 ${refreshingStockId === stock.id ? 'animate-spin' : ''}`} />
                                </Button>
                              )}
                              {!mobilePreview && !isViewer && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleEditStart(stock)}
                                  className="h-8 w-8 text-slate-400 hover:text-slate-900 hover:bg-slate-100"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                              )}
                              {!isViewer && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteStock(stock.id, stock.symbol)}
                                className={`text-slate-400 hover:text-red-600 hover:bg-red-50 ${mobilePreview ? 'h-6 w-6 p-0' : 'h-8 w-8'}`}
                              >
                                <Trash2 className={mobilePreview ? 'w-3 h-3' : 'w-4 h-4'} />
                              </Button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default YourStocksPanel;
